// src/components/ProjectManagement/QCTaskVerification.jsx
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, query, where, onSnapshot, updateDoc, doc, addDoc } from 'firebase/firestore';

const QCTaskVerification = ({ currentUser, userRole, projectId }) => {
  const [pendingTasks, setPendingTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // src/components/ProjectManagement/QCTaskVerification.jsx
// Update the useEffect to handle all projects

useEffect(() => {
  let q;
  if (projectId) {
    q = query(
      collection(db, 'tasks'), 
      where('projectId', '==', projectId),
      where('status', '==', 'completed'),
      where('qcVerified', '==', false)
    );
  } else {
    q = query(
      collection(db, 'tasks'), 
      where('status', '==', 'completed'),
      where('qcVerified', '==', false)
    );
  }
  
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const tasks = snapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data(),
      completedAtDate: doc.data().completedAt?.toDate?.() || new Date()
    }));
    setPendingTasks(tasks);
    setLoading(false);
  });
  
  return unsubscribe;
}, [projectId]);

  const handleApprove = async (task) => {
    if (!window.confirm(`Verify "${task.title}" as complete?`)) return;
    
    try {
      await updateDoc(doc(db, 'tasks', task.id), {
        qcVerified: true,
        qcVerifiedBy: currentUser.uid,
        qcVerifiedAt: new Date(),
        qcVerificationNote: 'Approved by QC',
        updatedAt: new Date()
      });
      
      // Notify worker
      if (task.assignedTo) {
        await addDoc(collection(db, 'notifications'), {
          userId: task.assignedTo,
          type: 'task_verified',
          title: 'Task Verified ✅',
          message: `"${task.title}" has been approved by QC`,
          taskId: task.id,
          projectId: task.projectId,
          verifiedBy: currentUser.uid,
          read: false,
          createdAt: new Date()
        });
      }
      
      alert('✅ Task verified and completed!');
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error approving task:', error);
      alert('Failed to verify task');
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    
    try {
      await updateDoc(doc(db, 'tasks', selectedTask.id), {
        status: 'ongoing',
        qcVerified: false,
        qcRejected: true,
        qcRejectedBy: currentUser.uid,
        qcRejectedAt: new Date(),
        qcRejectionReason: rejectionReason,
        updatedAt: new Date()
      });
      
      // Notify worker
      if (selectedTask.assignedTo) {
        await addDoc(collection(db, 'notifications'), {
          userId: selectedTask.assignedTo,
          type: 'task_rejected',
          title: 'Task Rejected ❌',
          message: `"${selectedTask.title}" needs rework. Reason: ${rejectionReason}`,
          taskId: selectedTask.id,
          projectId: selectedTask.projectId,
          rejectionReason: rejectionReason,
          rejectedBy: currentUser.uid,
          read: false,
          createdAt: new Date()
        });
      }
      
      alert('❌ Task rejected and sent back for rework');
      setShowRejectModal(false);
      setSelectedTask(null);
      setRejectionReason('');
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error rejecting task:', error);
      alert('Failed to reject task');
    }
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner"></div>
        <p>Loading tasks awaiting verification...</p>
      </div>
    );
  }

  return (
    <div className="qc-container">
      <div className="qc-header">
        <h2>🎛️ Quality Control Verification</h2>
        <p className="qc-subtitle">
          {pendingTasks.length} task{pendingTasks.length !== 1 ? 's' : ''} awaiting verification
        </p>
      </div>
      
      {pendingTasks.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">✅</div>
          <h3>No Tasks Awaiting Verification</h3>
          <p>All completed tasks have been verified</p>
          <small>When workers complete tasks, they will appear here</small>
        </div>
      ) : (
        <div className="tasks-verification-list">
          {pendingTasks.map(task => (
            <div key={task.id} className="qc-task-card">
              <div className="task-header">
                <h3>{task.title}</h3>
                <span className="task-status-badge pending-qc">Awaiting QC</span>
              </div>
              
              <p className="task-description">{task.description || 'No description provided'}</p>
              
              {task.assignmentNote && (
                <div className="assignment-note-display">
                  💬 <strong>Note from manager:</strong> {task.assignmentNote}
                </div>
              )}
              
              <div className="task-details">
                <div className="detail-item">
                  <span className="detail-icon">📁</span>
                  <span>Project: {task.projectName || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-icon">👤</span>
                  <span>Completed by: {task.assignedToNames || 'Unknown'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-icon">📅</span>
                  <span>Completed: {task.completedAtDate.toLocaleString()}</span>
                </div>
              </div>
              
              <div className="qc-actions">
                <button onClick={() => handleApprove(task)} className="approve-btn">
                  ✅ Approve
                </button>
                <button onClick={() => {
                  setSelectedTask(task);
                  setShowRejectModal(true);
                }} className="reject-btn">
                  ❌ Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <h3>❌ Reject Task</h3>
            <p><strong>Task:</strong> {selectedTask?.title}</p>
            
            <label>Reason for rejection:</label>
            <textarea 
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Explain why this task needs rework..."
              rows="4"
              className="assignment-note-input"
            />
            
            <div className="modal-actions">
              <button onClick={handleReject} className="reject-btn">
                Confirm Rejection
              </button>
              <button onClick={() => setShowRejectModal(false)} className="cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QCTaskVerification;